import type { ErrorEventView } from "@/lib/types";

/**
 * Human labels for the platform's error codes (backend {@code ErrorCodes}).
 *
 * The code is the stable identity an event is filtered and searched by; this
 * map is only how it reads on screen, which is why it lives in the client and
 * not in the database — wording is copy, and copy changes without a migration.
 * An unknown code (an older row, a newer backend) still renders: it falls back
 * to the code itself, and then to the producer's source, so a row is never
 * unlabelled.
 */
export const ERROR_CODE_LABELS: Record<string, string> = {
  "inbound.prompt_injection": "Prompt injection",
  "inbound.ambiguous_attribution": "Ambiguous reply",
  "import.failed": "Import failed",
  "import.rows_skipped": "Rows skipped",
  "import.rows_rejected": "Rows rejected",
  "import.column_mapping_mismatch": "Column mapping",
  "ingest.failed": "Ingestion failed",
  "ingest.paused": "Ingestion paused",
  "ingest.screen_failed": "Screen degraded",
  "job.dead": "Job failed",
  "job.paused": "Job paused",
  "api.unhandled": "Server error",
};

/** What to print on the kind badge. Never empty. */
export function errorKindLabel(event: ErrorEventView): string {
  if (event.code) return ERROR_CODE_LABELS[event.code] ?? event.code;
  return event.source;
}

/**
 * Where an event's "Open" action goes — the row an operator can actually act
 * on, not the machinery that failed.
 *
 * A prompt-injection warning points at the flagged message: from there the
 * reviewer reads it and can step through to the prospect, where the agent's
 * held reply is waiting for approval. Events pointing at a job or a request
 * have nothing to open, and get no link rather than a dead one.
 */
export function errorEventTarget(event: ErrorEventView): { to: string; label: string } | null {
  if (!event.refType) return null;
  switch (event.refType) {
    case "email":
      return event.refId ? { to: `/emails?message=${event.refId}`, label: "Open message" } : null;
    case "prospect":
      return event.refId ? { to: `/prospects/${event.refId}`, label: "Open prospect" } : null;
    case "ingest_job":
      return { to: "/knowledge", label: "Open ingestion" };
    default:
      return null;
  }
}
